import { ConditionalHelper } from './ConditionalHelper';
import { IRedirect } from '../models/helpers/redirect/IRedirect';
import { IRedirectConditional } from '../models/helpers/redirect/IRedirectConditional';
import { replacePlaceholders } from '../utils/replacePlaceholders';
import { IRedirectOptions } from '../models/helpers/redirect/IRedirectOptions';
import { isArrayLike } from '../utils/isArrayLike';
import { changeCase } from '../utils';
import { isNullOrEmpty } from '../utils/typeChecks';
import { filterOutEmpty } from '../utils/collection';
import { IRedirectParamCase } from '../models/helpers/redirect/IRedirectParamCase';

export class RedirectHelper {
	url: URL;
	constructor(url: string, base?: string) {
		if (!base) {
			base =  window && window.location.origin;
		}
		this.url = new URL(url, base);
	}

	go(redirect: IRedirect, inputs?: object) {
		const { options } = redirect;
		const redirectPath = this.getRedirectPath(redirect, inputs);
		if (redirectPath) {
			if (options && options.target) {
				window.open(redirectPath, options.target);
			}
			else {
				document.location.href = redirectPath;
			}
		}
	}

	getRedirectPath(redirect: IRedirect | undefined, inputs?: object) {
		if (redirect) {
			const { conditionalRedirects, defaultUrl, base, options = {} } = redirect;
			let validRedirect: IRedirectConditional | undefined;
			if (conditionalRedirects) {
				validRedirect = conditionalRedirects.find((conditionalRedirect) => {
					const { conditional } = conditionalRedirect;
					const conditionalHelper = new ConditionalHelper(conditional);
					return conditionalHelper.matches(inputs);
				});
			}
			const url = (validRedirect && validRedirect.url) || defaultUrl;
			const redirectOptions = (validRedirect && validRedirect.options) || options;
			const replacedUrl = replacePlaceholders(inputs || {}, url);
			return this.getRedirectUrl(replacedUrl, base, redirectOptions, inputs);
		}
	}

	getRedirectUrl(redirectString: string, redirectBase: string, options: IRedirectOptions, inputs?: object): string {
		const base = options.preserveOrigin ? this.url.origin : redirectBase;
		const redirectUrl = new URL(redirectString, base || window && window.location.origin);
		this.updatePathname(redirectUrl, options, inputs);
		this.updateSearchQueryString(redirectUrl, options, inputs);
		this.updateHash(redirectUrl, options);
		return redirectUrl.toString();
	}

	private updatePathname(redirectUrl: URL, options: IRedirectOptions, inputs?: object) {
		const redirectPathSegments = [];
		const paramCaseOptions: IRedirectParamCase = Object.assign({ apply: false, separator: '-' }, options.paramCase);

		// prepend path with origin path
		if (options.preserveOriginPath) {
			const originUrlSegments = this.url.pathname.split('/');
			redirectPathSegments.push(...originUrlSegments);
		}

		// apply pathname derived from static redirect pathname
		redirectPathSegments.push(...redirectUrl.pathname.split('/'));

		// append dynamic pathname
		if (isArrayLike(options.urlParams) && options.urlParams.length > 0) {
			const dynamicPathSegments = this.replacePlaceholdersOnCollection(options.urlParams, inputs);
			redirectPathSegments.push(...this.applyParamCaseToCollection(dynamicPathSegments, paramCaseOptions));
		}

		// finally append dynamic endpoint
		if (options.urlEndpoint && isArrayLike(options.urlEndpoint) && options.urlEndpoint.length > 0) {
			const endpointValues = options.urlEndpoint;
			const endpointSegments: string[] = this.replacePlaceholdersOnCollection(endpointValues, inputs);
			const endpoint = this.applyParamCaseToCollection(endpointSegments, paramCaseOptions).join(paramCaseOptions.separator);
			redirectPathSegments.push(endpoint);
		}

		// merge pathname segments to save final pathname
		const reducedPathSegments = redirectPathSegments.reduce((segments: string[], segment: string, segmentIndex: number) => {
			if (segment !== '' && (segmentIndex === 0 || segmentIndex > 0 && redirectPathSegments[segmentIndex - 1] !== segment)) {
				segments.push(segment);
			}
			return segments;
		}, []);

		// save
		redirectUrl.pathname = reducedPathSegments.join('/');
	}

	private updateSearchQueryString(redirectUrl: URL, options: IRedirectOptions, inputs?: object) {
		let redirectSearchParamsSegments = this.getSearchParamsSegments(redirectUrl);

		// override origin with static redirect search params
		if (options.preserveOriginQueryString) {
			const originSearchParams = this.getSearchParamsSegments(this.url);
			redirectSearchParamsSegments = Object.assign({}, originSearchParams, redirectSearchParamsSegments);
		}

		// override static redirect with dynamic search params
		if (isArrayLike(options.queryStringParams) && options.queryStringParams.length > 0) {
			const dynamicSearchParams = options.queryStringParams.reduce((params, param) => {
				const replacedValue = replacePlaceholders(inputs || {}, param.value);
				if (!param.optional || (param.optional && !isNullOrEmpty(replacedValue))) {
					params[param.key] = replacedValue;
				}
				return params;
			}, {});
			redirectSearchParamsSegments = Object.assign({}, redirectSearchParamsSegments, dynamicSearchParams);
		}

		// save search params segments
		const searchParamString = this.getSearchParamsString(redirectSearchParamsSegments);
		if (searchParamString) {
			redirectUrl.search = searchParamString;
		}
	}

	private updateHash(redirectUrl: URL, options: IRedirectOptions) {
		if (!redirectUrl.hash && options.preserveOriginHash) {
			redirectUrl.hash = this.url.hash;
		}
	}

	private applyParamCaseToCollection(collection: string[], options: IRedirectParamCase) {
		if (options.apply) {
			return collection.map(value => changeCase.paramCase(value, { delimiter: options.separator }));
		}
		return collection;
	}

	private replacePlaceholdersOnCollection(collection: string[], inputs?: object) {
		let updatedCollection = collection;
		if (inputs) {
			updatedCollection = updatedCollection.map(value => replacePlaceholders(inputs || {}, value));
		}
		return filterOutEmpty(updatedCollection);
	}

	private getSearchParamsString(searchParams: {[key: string]: string | undefined}): string {
		const paramKeys = Object.keys(searchParams);
		if (paramKeys.length > 0) {
			const params = paramKeys.map(key => !isNullOrEmpty(searchParams[key]) ? `${key}=${searchParams[key]}` : key);
			return `?${params.join('&')}`;
		}
		return;
	}

	private getSearchParamsSegments(url: URL): {[key: string]: string | undefined} {
		let params = {};
		if (url && url.search) {
			const questionMarkStripped = url.search.replace('?', '');
			if (questionMarkStripped) {
				const paramSegments = questionMarkStripped.split('&');
				if (paramSegments) {
					params = paramSegments.reduce((params, param) => {
						const paramParts = param.split('=');
						if (paramParts[0]) {
							params[paramParts[0]] = paramParts[1] && decodeURI(paramParts[1]) || undefined;
						}
						return params;
					}, {});
				}
			}
		}
		return params;
	}
}